<!-- SettingsView.vue -->
<template>
    <div class="app-container">
      <header-bar></header-bar>

      <div class="content-wrapper">
        <div class="empty-left"></div>
        <div class="left-sidenotes">
          <!-- Left side: Settings navigation -->
          <h2 class="settings-header">Settings</h2>
          <p
            class="subhead"
            :class="{ active: activeSection === 'general' }"
            @click="setActiveSection('general')"
          >
            General Information
          </p>
          <p
            class="subhead"
            :class="{ active: activeSection === 'news' }"
            @click="setActiveSection('news')"
          >
            News Sources
          </p>
        </div>
  
        <div class="main">
          <!-- Only visible on screens under 768px -->
            <div class="mobile-select">
                <select v-model="activeSection">
                <option value="general">General Information</option>
                <option value="news">News Sources</option>
                </select>
            </div>  

          <!-- Right side: actual components -->
          <GeneralInformation
            v-if="activeSection === 'general'"
            :userInfo="userInfo"
            :loading="loading"
            :saving="saving"
            @updateUserInfo="updateUserInfo"
            @saveUserInfo="saveUserInfo"
          />
          <NewsSources
            v-else-if="activeSection === 'news'"
            :userInfo="userInfo"
            :saving="saving"
            @updateUserInfo="updateUserInfo"
            @saveUserInfo="saveUserInfo"
          />
        </div>
        <div class="right-sidenotes"></div>
      </div>
    </div>
  </template>
  
  <script>
  import HeaderBar from './RevisedHeader.vue';
  import GeneralInformation from './Settings Components/GeneralInformation.vue';
  import NewsSources from './Settings Components/NewsSources.vue';
  import { onAuthStateChanged } from 'firebase/auth';
  import { doc, getDoc, updateDoc } from 'firebase/firestore';
  import { auth, db } from '@/firebase';
  
  export default {
    name: 'SettingsView',
    components: {
      HeaderBar,
      GeneralInformation,
      NewsSources
    },
    
    data() {
      return {
        activeSection: 'general',
        loading: true,
        saving: false,
        userInfo: {
          displayName: '',
          address: {
            line1: '',
            line2: '',
            city: '',
            state: '',
            zip: ''
          },
          townSettings: {
            selectBoard: false,
            schoolBoard: false,
            otherTownMeetings: false,
            commissionGroup: false,
            upcomingMeetings: false,       // New setting
            minutesOnly: false      // New setting
          },
          publications: {
            vtDigger: false,
            sevenDays: false,
            addisonIndependent: false,
            vtPublic: false,
            brandonReporter: false,
          }
        }
      };
    },
    created() {
      onAuthStateChanged(auth, (user) => {
        if (user) {
          this.fetchUserInfo(user.uid);
        } else {
          // console.log('No user authenticated');
          this.loading = false;
        }
      });
    },
    methods: {
      setActiveSection(section) {
        this.activeSection = section;
      },
      async fetchUserInfo(userId) {
        try {
          const userDoc = await getDoc(doc(db, 'users', userId));
          if (userDoc.exists()) {
            const data = userDoc.data();
            // Populate userInfo with whatever's in Firestore
            if (data.displayName) this.userInfo.displayName = data.displayName;
            if (data.address) {
              this.userInfo.address = { ...this.userInfo.address, ...data.address };
            }
            if (data.enabledTownSettings && Array.isArray(data.enabledTownSettings)) {
              this.userInfo.townSettings.selectBoard = data.enabledTownSettings.includes('selectBoard');
              this.userInfo.townSettings.schoolBoard = data.enabledTownSettings.includes('schoolBoard');
              this.userInfo.townSettings.otherTownMeetings = data.enabledTownSettings.includes('otherTownMeetings');
              this.userInfo.townSettings.commissionGroup = data.enabledTownSettings.includes('commissionGroup');
              this.userInfo.townSettings.upcomingMeetings = data.enabledTownSettings.includes('upcomingMeetings');       // New
              this.userInfo.townSettings.minutesOnly = data.enabledTownSettings.includes('minutesOnly'); // New
            }
            if (data.enabledPublications && Array.isArray(data.enabledPublications)) {
              this.userInfo.publications.vtDigger = data.enabledPublications.includes('vtDigger');
              this.userInfo.publications.sevenDays = data.enabledPublications.includes('sevenDays');
              this.userInfo.publications.addisonIndependent = data.enabledPublications.includes('addisonIndependent');
              this.userInfo.publications.vtPublic = data.enabledPublications.includes('vtPublic');
              this.userInfo.publications.brandonReporter = data.enabledPublications.includes('brandonReporter');  // Added Brandon Reporter

            }
          }
        } catch (err) {
          console.error('Error fetching user:', err);
        } finally {
          this.loading = false;
        }
      },
      updateUserInfo(newData) {
        // Merges child updates into local userInfo
        this.userInfo = { ...this.userInfo, ...newData };
      },
      async saveUserInfo() {
        if (!auth.currentUser) {
          console.error('No authenticated user');
          return;
        }
        
        this.saving = true;
        try {
          const { city } = this.userInfo.address;
          const allowedTowns = ['Brandon', 'Goshen'];
          if (!allowedTowns.some(town => town.toLowerCase() === city.trim().toLowerCase())) {
            alert("Invalid town. Only Brandon and Goshen are allowed.");
            this.saving = false;
            return;
          }
          const enabledTownSettings = Object.entries(this.userInfo.townSettings)
            .filter(([, val]) => val)
            .map(([key]) => key);
  
          const enabledPublications = Object.entries(this.userInfo.publications)
            .filter(([, val]) => val)
            .map(([key]) => key);
  
          const updateData = {
            displayName: this.userInfo.displayName,
            address: this.userInfo.address,
            enabledTownSettings,
            enabledPublications,
            lastUpdated: new Date()
          };
  
          await updateDoc(doc(db, 'users', auth.currentUser.uid), updateData);
          alert('Information saved successfully!');
        } catch (err) {
          console.error('Error saving user info:', err);
          alert('Couldn’t save your info. Try again maybe?');
        } finally {
          this.saving = false;
        }
      }
    }
  };
  </script>
  
  <style scoped>
  .app-container {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100vh;
    overflow: hidden;
  }
  
  .content-wrapper {
    position: relative;
    flex: 1;
    display: flex;
    overflow: hidden;
    padding-top: 64px;
  }
  
  .empty-left {
    display: flex;
    flex: 1;
    background-color: #fffff8;
  }
  
  .right-sidenotes, .left-sidenotes {
    display: flex;
    flex-direction: column;
    flex: 1;
    background-color: #fffff8;
    padding: 20px; /* to space out the text */
  }
  
  .main {
    flex: 2;
    background-color: #fffff8;
    display: flex;
    flex-direction: column;
    overflow: auto;
    max-width: 1200px;
  }
  
  .settings-header {
    font-size: 32px;
    font-weight: 600;
    margin-bottom: 24px;
    color: #545051;
  }
  
  .subhead {
    font-size: 1rem;
    font-weight: 400;
    cursor: pointer;
    color: #545051;
    margin: 0 0 16px 0;
    padding: 0;
    line-height: 1.5;
  }
  
  .subhead.active {
    text-decoration: underline dashed;
    text-underline-offset: 2px;
  }

  @media (max-width: 768px) {
  .mobile-select {
    display: block;
  }
  .empty-left,
  .left-sidenotes,
  .right-sidenotes {
    display: none;
  }
  .main {
    flex: 1;
    max-width: 100%;
    padding: 0 1rem;
  }
}

@media (min-width: 768px) {
  .mobile-select {
    display: none;
  }
  .empty-left, .right-sidenotes {
    display: none;
  }
  .left-sidenotes {
    display: flex;
  }
  .main {
    flex: 2;
    max-width: 100%;
    padding: 0 2rem;
  }
}

@media (min-width: 1024px) {
  .empty-left, .right-sidenotes, .left-sidenotes {
    display: flex;
  }
  .main {
    flex: 3;
    max-width: 1200px;
    padding: 0;
  }
}

.mobile-select {
  margin-bottom: 24px;
  position: relative;
  width: max-content;
}

.mobile-select select {
  font-size: 32px;
  font-weight: 600;
  color: #545051;
  padding-top: 24px;
  margin-left:24px;
  border: none;
  border-bottom: 1px dashed #545051;
  background-color: #fffff8;
  cursor: pointer;
  text-underline-offset: 2px;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  /* Remove any width constraints */
  width: 100%;
  
}

.mobile-select select:focus {
  outline: none;
  border-bottom: 2px dashed #545051;    
  margin-left: 24px;
  
}
  
  </style>
  